import ngeohash from 'ngeohash';

/** Light (default) and dark variants. Must match pg-lambda AD_IMAGE_URL_FIELDS. */
export const AD_IMAGE_URL_FIELDS = Object.freeze([
  'imageUrl',
  'imageUrlDark',
  'wideBannerImageUrl',
  'wideBannerImageUrlDark',
]);

/** Must match the allowed list in pg-lambda's advertising handler. */
export const AD_APP_TYPES = [
  { value: 'Playground', label: 'Playground' },
  { value: 'Castle', label: 'Castle' },
  { value: 'ChristmasMarkets', label: 'Christmas Markets' },
  { value: 'Waterfall', label: 'Waterfall' },
];

/**
 * Selected app types in the canonical order; unknown values are kept at the end
 * so the backend can reject them with its own message.
 * @param {object} ad
 */
export function normalizeAdAppTypes(ad) {
  if (!ad || !Array.isArray(ad.appTypes)) return [];
  const selected = new Set(ad.appTypes.map((t) => String(t).trim()).filter(Boolean));
  const known = AD_APP_TYPES.map((t) => t.value).filter((v) => selected.has(v));
  const unknown = [...selected].filter((v) => !known.includes(v));
  return [...known, ...unknown];
}

/** @param {object} ad @param {string} field */
export function adImageUrl(ad, field) {
  const value = ad?.[field];
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * True when the ad has at least one light creative and one dark creative,
 * so the list can offer a light/dark switch.
 * @param {object} ad
 */
export function adHasLightAndDarkImages(ad) {
  const light = adImageUrl(ad, 'imageUrl') || adImageUrl(ad, 'wideBannerImageUrl');
  const dark = adImageUrl(ad, 'imageUrlDark') || adImageUrl(ad, 'wideBannerImageUrlDark');
  return Boolean(light && dark);
}

/**
 * Image to show for a theme. Dark prefers a dark asset so the switch
 * actually changes the picture; light prefers the default assets.
 * @param {object} ad
 * @param {'light'|'dark'} theme
 * @returns {{ src: string, kind: 'wide'|'logo' } | null}
 */
export function adPreviewImage(ad, theme = 'light') {
  const lightWide = adImageUrl(ad, 'wideBannerImageUrl');
  const darkWide = adImageUrl(ad, 'wideBannerImageUrlDark');
  const lightLogo = adImageUrl(ad, 'imageUrl');
  const darkLogo = adImageUrl(ad, 'imageUrlDark');
  const iconUrl = typeof ad?.icon?.url === 'string' ? ad.icon.url.trim() : '';

  const ordered =
    theme === 'dark'
      ? [
          [darkWide, 'wide'],
          [darkLogo, 'logo'],
          [lightWide, 'wide'],
          [lightLogo, 'logo'],
          [iconUrl, 'logo'],
        ]
      : [
          [lightWide, 'wide'],
          [lightLogo, 'logo'],
          [iconUrl, 'logo'],
          [darkWide, 'wide'],
          [darkLogo, 'logo'],
        ];

  const match = ordered.find(([src]) => src);
  return match ? { src: match[0], kind: match[1] } : null;
}

/** @param {object} ad */
export function normalizeAdGeohashes(ad) {
  if (!ad) return [];
  if (Array.isArray(ad.geohashes) && ad.geohashes.length > 0) {
    return ad.geohashes.map((g) => String(g).trim()).filter(Boolean);
  }
  if (ad.geohash != null && String(ad.geohash).trim() !== '') {
    return [String(ad.geohash).trim()];
  }
  return [];
}

/**
 * Prepare advertising record for POST /admin?cmd=advertising
 * @param {object} ad
 */
export function buildAdvertisingSavePayload(ad) {
  const hashes = Array.isArray(ad.geohashes)
    ? ad.geohashes.map((g) => String(g).trim()).filter(Boolean)
    : normalizeAdGeohashes(ad);

  const payload = {
    ...ad,
    geohashes: hashes,
    message: (ad.tagline || '').trim(),
    appTypes: normalizeAdAppTypes(ad),
  };

  if (hashes.length > 0) {
    payload.geohash = hashes[0];
  } else {
    delete payload.geohash;
  }

  const icon = ad.icon;
  if (!icon || (typeof icon === 'object' && !icon.url && !icon.key)) {
    delete payload.icon;
  }

  for (const field of AD_IMAGE_URL_FIELDS) {
    const value = adImageUrl(payload, field);
    if (value) payload[field] = value;
    else delete payload[field];
  }

  return payload;
}

/**
 * Message for a failed POST /admin?cmd=advertising; the API's 400 body is { error, allowed }.
 * @param {object} err rejection from saveAdvertising
 */
export function getAdvertisingSaveErrorMessage(err) {
  const data = err?.data;
  if (!data || typeof data.error !== 'string' || !data.error.trim()) {
    return 'Failed to save ad';
  }
  if (Array.isArray(data.allowed) && data.allowed.length > 0) {
    return `${data.error} (allowed: ${data.allowed.join(', ')})`;
  }
  return data.error;
}

export function getGeohashRadiusMiles(geohash) {
  if (!geohash) return null;
  try {
    const bbox = ngeohash.decode_bbox(geohash);
    const [minLat, minLon, maxLat, maxLon] = bbox;
    const milesPerLat = 69.0;
    const milesPerLon = 69.172 * Math.cos(((minLat + maxLat) / 2) * (Math.PI / 180));
    const width = Math.abs(maxLon - minLon) * milesPerLon;
    const height = Math.abs(maxLat - minLat) * milesPerLat;
    const radius = Math.min(width, height) / 2;
    return radius.toFixed(2);
  } catch {
    return null;
  }
}
